import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import AuthShell from './AuthShell';
import { apiRequest, handleSessionRedirect, handleBranding } from '../../utils';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [isAccountRequired, setIsAccountRequired] = useState(
    sessionStorage.getItem('isAccountRequired') !== '0'
  );

  const hasOtherEventId = Boolean(sessionStorage.getItem('otherEventId'));
  const showNameFields = hasOtherEventId && !isAccountRequired;

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const data = await apiRequest('GET', '/check-auth');
        if (data.message === 'Authenticated') {
          handleSessionRedirect(navigate, true);
        }
      } catch (e) {
        console.error('Error checking authentication:', e.message);
      }
    };

    checkAuth();

    handleBranding().then(() => {
      setIsAccountRequired(sessionStorage.getItem('isAccountRequired') !== '0');
    });

    const toastMessage = sessionStorage.getItem('toastMessage');
    if (toastMessage) {
      setError(t(toastMessage));
      setShowToast(true);
      sessionStorage.removeItem('toastMessage');
    }
  }, [navigate, t]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData.entries());

    // If the visitor arrived via a branded storefront in this session, pin the login
    // to that business (server routes straight to its home, never the cross-business
    // picker). Absent on a cold tornea.app/login → cross-business routing.
    const brandLoginSlug = sessionStorage.getItem('brandLoginSlug') || null;

    try {
      const data = await apiRequest('POST', '/login', { ...values, brand_slug: brandLoginSlug });
      setError(null);
      sessionStorage.removeItem('assignedEventId');
      sessionStorage.removeItem('assignedTeamId');
      sessionStorage.removeItem('assignedCompanyId');

      // Tells the NavBar (and any other UI gated on auth) it can show now.
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));

      // These session params were consumed by THIS login. They persist in
      // sessionStorage (usePersistUrlParams never clears them) and ride along as
      // headers on every later request — a stale `otherEventId` makes the next
      // login event-scoped and can drop the user out of their real team/event
      // context (blank Home). Clear them now, like the assigned* ones above, so
      // a later cold login (e.g. after a password reset) resolves cleanly.
      const redirect = sessionStorage.getItem('redirect') || data.redirect || '/home';
      sessionStorage.removeItem('redirect');
      sessionStorage.removeItem('otherEventId');
      // Consumed by THIS login — clear it (like otherEventId) so a later cold login
      // in the same session isn't wrongly scoped to a brand visited earlier.
      sessionStorage.removeItem('brandLoginSlug');
      navigate(redirect);
    } catch (err) {
      setError(err.message);
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('welcomeBack', { defaultValue: t('login') })}
      subtitle={t('loginSubtitle', { defaultValue: 'Sign in to keep the energy going.' })}
      footer={
        // Self-registration disabled — signup link removed 2026-06-18
        null
        /*
        <p className="auth__footer">
          {t('noAccountYet', { defaultValue: "Don't have an account?" })}
          <Link to="/register" className="auth__footer-link">{t('register')}</Link>
        </p>
        */
      }
    >
      <form className="auth__card" onSubmit={onSubmit}>
        {showNameFields && (
          <div className="auth__row">
            <div className="auth__field">
              <label className="auth__label">{t('firstName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  name="first_name"
                  placeholder={t('enterFirstName')}
                  required
                  autoComplete="given-name"
                />
              </div>
            </div>
            <div className="auth__field">
              <label className="auth__label">{t('lastName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  name="name"
                  placeholder={t('enterLastName')}
                  required
                  autoComplete="family-name"
                />
              </div>
            </div>
          </div>
        )}

        <div className="auth__field">
          <label className="auth__label">{t('email')}</label>
          <div className="auth__input-wrap">
            <input
              className="auth__input"
              type="text"
              name="email"
              placeholder={t('enterEmail')}
              required
              autoComplete="email"
            />
          </div>
        </div>

        <div className="auth__field">
          <label className="auth__label">{t('password')}</label>
          <div className="auth__input-wrap">
            <input
              className="auth__input auth__input--with-eye"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder={t('enterPassword')}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="auth__eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={t(showPassword ? 'hidePassword' : 'showPassword', { defaultValue: 'Toggle password visibility' })}
            >
              <Icon name={showPassword ? 'eye-crossed' : 'eye'} />
            </button>
          </div>
        </div>

        <Link to="/forgot-password" className="auth__forgot">
          {t('forgotPassword')}
        </Link>

        <button type="submit" className="auth__primary" disabled={submitting}>
          {submitting ? <span className="auth__spinner" /> : <Icon name="sign-in-alt" />}
          {t('login')}
        </button>

        <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
      </form>
    </AuthShell>
  );
};

export default Login;
